import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon, type IconName } from './icons.js';

/**
 * NAV — agrupada por área funcional para facilitar la lectura:
 *   - Overview: resumen general del producto.
 *   - Observability: lo que está pasando ahora en runtime (costos, errores, jobs, auditoría).
 *   - Quality: análisis de calidad de sesiones (destilados, findings, regresión, simulador).
 *   - Diccionario: el registro de faltantes (encargo 49).
 *   - Configuration: lo que se configura del producto (módulos, routing, providers, pricing, billing).
 *   - System: utilidades de plataforma.
 */
interface NavEntry {
  kind: 'link';
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}
interface NavSection {
  kind: 'section';
  label: string;
}
type NavItem = NavEntry | NavSection;

const NAV: NavItem[] = [
  { kind: 'section', label: 'Overview' },
  { kind: 'link', to: '/', label: 'Dashboard', icon: 'grid', end: true },
  { kind: 'link', to: '/sessions', label: 'Sessions', icon: 'session' },
  { kind: 'link', to: '/users', label: 'Users', icon: 'users' },

  { kind: 'section', label: 'Observability' },
  { kind: 'link', to: '/costs', label: 'Costs', icon: 'bar' },
  { kind: 'link', to: '/reports', label: 'Reports', icon: 'bar' },
  { kind: 'link', to: '/errors', label: 'Errors', icon: 'alert' },
  { kind: 'link', to: '/jobs', label: 'Jobs', icon: 'cog' },
  { kind: 'link', to: '/audit', label: 'Audit', icon: 'shield' },
  { kind: 'link', to: '/emails', label: 'Emails', icon: 'mail' },

  { kind: 'section', label: 'Quality' },
  { kind: 'link', to: '/distill', label: 'Destilados', icon: 'trace' },
  { kind: 'link', to: '/findings', label: 'Findings', icon: 'search' },
  { kind: 'link', to: '/findings/shipped', label: 'Shipped fixes', icon: 'shield' },
  { kind: 'link', to: '/simulator', label: 'Simulator', icon: 'layers' },
  { kind: 'link', to: '/ai-reports', label: 'Reportes de IA', icon: 'alert' },

  { kind: 'section', label: 'Diccionario' },
  { kind: 'link', to: '/dictionary/reports', label: 'Avisos', icon: 'alert' },
  { kind: 'link', to: '/dictionary/missing', label: 'Faltantes', icon: 'search' },

  { kind: 'section', label: 'Configuration' },
  { kind: 'link', to: '/modules', label: 'Modules', icon: 'layers' },
  { kind: 'link', to: '/routing', label: 'Routing', icon: 'plug' },
  { kind: 'link', to: '/providers', label: 'Providers', icon: 'plug' },
  { kind: 'link', to: '/pricing', label: 'Pricing', icon: 'tag' },
  { kind: 'link', to: '/billing/config', label: 'Billing', icon: 'tag' },
  { kind: 'link', to: '/analyze/config', label: 'Analyze', icon: 'cog' },

  { kind: 'section', label: 'News' },
  { kind: 'link', to: '/news', label: 'Artículos', icon: 'layers', end: true },
  { kind: 'link', to: '/news/sources', label: 'Fuentes', icon: 'plug' },

  { kind: 'section', label: 'Scout' },
  { kind: 'link', to: '/scout', label: 'Búsquedas', icon: 'search' },

  { kind: 'section', label: 'Fit' },
  { kind: 'link', to: '/fit/videos', label: 'Videos', icon: 'layers' },

  { kind: 'section', label: 'System' },
  { kind: 'link', to: '/system', label: 'System', icon: 'wrench' },
];

export function Sidebar(): ReactElement {
  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-brand">
        <span className="sidebar-brand-dot" aria-hidden="true" />
        <span>Orborix</span>
        <span className="sidebar-brand-version mono">bo · v0.4</span>
      </div>
      <nav className="sidebar-section" aria-label="Backoffice navigation">
        <div className="sidebar-nav">
          {NAV.map((item, idx) =>
            item.kind === 'section' ? (
              <div
                key={`section-${idx}`}
                className="sidebar-section-label"
                role="heading"
                aria-level={2}
              >
                {item.label}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) => (isActive ? 'is-active' : '')}
              >
                <Icon name={item.icon} className="sidebar-nav-icon" />
                <span>{item.label}</span>
              </NavLink>
            ),
          )}
        </div>
      </nav>
      <div className="sidebar-footer">
        <span className="flex ai-c gap-3">
          <span className="dot" />
          <span className="mono">api · ok</span>
        </span>
        <span className="mono">v0.1.0</span>
      </div>
    </aside>
  );
}
